import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { BookOpen, FileSearch, Brain, GraduationCap, ArrowRight, Lock, Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAccess } from "@/hooks/useAccess";
import { SiteHeader } from "@/components/SiteHeader";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PastDueBanner } from "@/components/PastDueBanner";
import { getParentLockStatus, setParentPin, verifyParentPin } from "@/lib/parent-lock.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/app")({
  head: () => ({
    meta: [
      { title: "Your tools — SmartEssay" },
      { name: "description", content: "Choose a tool: Outline Generator, Essay Reviewer, or SmartEssay.AI Tutor." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HubPage,
});

const SESSION_KEY = "parent_lock_unlocked";

type Tool = {
  to: "/generator" | "/reviewer" | "/tutor";
  title: string;
  tagline: string;
  description: string;
  icon: typeof BookOpen;
  gated: boolean;
};

function HubPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  const getStatus = useServerFn(getParentLockStatus);
  const verify = useServerFn(verifyParentPin);
  const setPin = useServerFn(setParentPin);

  const [accountType, setAccountType] = useState<"adult" | "student">("adult");
  const [hasPin, setHasPin] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const [pinDialogFor, setPinDialogFor] = useState<Tool["to"] | null>(null);
  const [pin, setPinVal] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/auth" });
      else {
        setUserId(data.session.user.id);
        setChecking(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/auth" });
      else setUserId(session.user.id);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await getStatus();
        if (cancelled) return;
        setAccountType(status.accountType);
        setHasPin(status.hasPin);
        if (typeof window !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1") {
          setUnlocked(true);
        }
      } catch {
        // ignore — gate on the tool page will surface errors
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, getStatus]);

  const access = useAccess(userId);

  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  const tools: Tool[] = [
    {
      to: "/generator",
      title: "Generator",
      tagline: "From outline to completed sample essay",
      description: "Build an outline, then expand it into a full sample essay with real cited evidence and tone controls.",
      icon: BookOpen,
      gated: true,
    },
    {
      to: "/reviewer",
      title: "Essay Reviewer",
      tagline: "Get structured feedback",
      description: "Upload or paste a draft. Get evidence gaps, flow analysis, and prioritized improvements.",
      icon: FileSearch,
      gated: true,
    },
    {
      to: "/tutor",
      title: "SmartEssay.AI Tutor",
      tagline: "You write — we coach",
      description: "A guided conversation that helps you think through your essay without writing it for you.",
      icon: Brain,
      gated: false,
    },
  ];

  const subLocked = !access.loading && !access.isSubscribed && !access.hasAccess;
  const pinLocked = accountType === "student" && !unlocked;

  const handleCardClick = (e: React.MouseEvent, t: Tool) => {
    if (t.gated && pinLocked) {
      e.preventDefault();
      setPinVal("");
      setPin2("");
      setPinDialogFor(t.to);
    }
  };

  const closeDialog = () => {
    setPinDialogFor(null);
    setPinVal("");
    setPin2("");
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinDialogFor) return;
    setBusy(true);
    try {
      const res = await verify({ data: { pin } });
      if (!res.ok) {
        toast.error("Incorrect PIN.");
        return;
      }
      sessionStorage.setItem(SESSION_KEY, "1");
      setUnlocked(true);
      const to = pinDialogFor;
      closeDialog();
      navigate({ to });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinDialogFor) return;
    if (pin !== pin2) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await setPin({ data: { newPin: pin } });
      sessionStorage.setItem(SESSION_KEY, "1");
      setHasPin(true);
      setUnlocked(true);
      const to = pinDialogFor;
      closeDialog();
      toast.success("Access PIN set.");
      navigate({ to });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not set PIN");
    } finally {
      setBusy(false);
    }
  };

  const dialogTool = tools.find((t) => t.to === pinDialogFor);

  return (
    <div className="min-h-screen bg-hero-gradient">
      <PaymentTestModeBanner />
      <PastDueBanner show={access.isPastDue} />
      <SiteHeader />

      <main className="mx-auto max-w-5xl px-6 pb-24 pt-10">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            <GraduationCap className="h-3.5 w-3.5" /> Welcome back
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            What are you working on today?
          </h1>
          <p className="mt-3 text-muted-foreground">
            Pick a tool to get started. You can switch between them anytime.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((t) => {
            const { to, title, tagline, description, icon: Icon, gated } = t;
            const showLock = gated && (pinLocked || (to === "/generator" && subLocked));
            return (
              <Link
                key={to}
                to={to}
                onClick={(e) => handleCardClick(e, t)}
                className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-glow"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <h2 className="font-display text-xl font-semibold">{title}</h2>
                  {showLock && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
                <p className="mt-0.5 text-xs uppercase tracking-wider text-primary/80">{tagline}</p>
                <p className="mt-3 flex-1 text-sm text-muted-foreground">{description}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                  {gated && pinLocked ? "Unlock" : "Open"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>

      </main>

      <Dialog open={pinDialogFor !== null} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {hasPin ? <Lock className="h-5 w-5 text-primary" /> : <ShieldCheck className="h-5 w-5 text-primary" />}
              {hasPin ? "Access PIN required" : "Set an access PIN"}
            </DialogTitle>
            <DialogDescription>
              {hasPin
                ? `The ${dialogTool?.title ?? "tool"} is locked. Enter your access PIN to unlock it for this session.`
                : `Before students can use the ${dialogTool?.title ?? "tool"}, set an access PIN. The Tutor stays open without a PIN.`}
            </DialogDescription>
          </DialogHeader>

          {hasPin ? (
            <form onSubmit={handleUnlock} className="space-y-3">
              <div>
                <Label htmlFor="hub-pin">Access PIN</Label>
                <Input
                  id="hub-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPinVal(e.target.value.replace(/\D/g, ""))}
                  placeholder="4–8 digits"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || pin.length < 4}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Unlock {dialogTool?.title}
              </Button>
              <p className="text-xs text-muted-foreground">
                Forgot the PIN? Reset it from Account → Access lock.
              </p>
            </form>
          ) : (
            <form onSubmit={handleSetup} className="space-y-3">
              <div>
                <Label htmlFor="hub-newpin">New access PIN</Label>
                <Input
                  id="hub-newpin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPinVal(e.target.value.replace(/\D/g, ""))}
                  placeholder="4–8 digits"
                  required
                />
              </div>
              <div>
                <Label htmlFor="hub-newpin2">Confirm PIN</Label>
                <Input
                  id="hub-newpin2"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,8}"
                  maxLength={8}
                  value={pin2}
                  onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy || pin.length < 4}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Set access PIN
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
